import { backgroundOrange, blackOrange, blue, darkOrange, orange, white } from '@/theme/colors'
import { fontBody, fontDisplay } from '@/theme/fonts'
import QRCode from 'react-qr-code'
import styled, { keyframes } from 'styled-components'

// Warm supporting tints from the login design handoff (docs/design_handoff_orbit_login)
const inkSoft = '#8a5238'
const inkLede = '#7a4a30'
const inkFaint = '#b98a6e'
const glowPeach = '#ffe6d2'

export const Page = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	overflow-y: auto;
	color: ${blackOrange};
	font-family: ${fontBody};
	-webkit-font-smoothing: antialiased;
	background:
		radial-gradient(900px 420px at 50% -160px, ${glowPeach} 0%, rgba(255, 230, 210, 0) 70%),
		${backgroundOrange};
`

export const TopBar = styled.header`
	flex: none;
	display: flex;
	align-items: center;
	justify-content: space-between;
	max-width: 980px;
	width: 100%;
	margin: 0 auto;
	padding: 26px 28px;

	@media (max-width: 760px) {
		padding: 20px;
	}
`

export const Brand = styled.a`
	display: flex;
	align-items: center;
	gap: 11px;
	text-decoration: none;

	img {
		width: 32px;
		height: 32px;
	}

	b {
		font-family: ${fontDisplay};
		font-weight: 900;
		font-size: 21px;
		color: ${blackOrange};
		letter-spacing: -0.02em;
	}
`

export const HelpLink = styled.a`
	font-size: 14px;
	font-weight: 500;
	color: ${inkSoft};
	text-decoration: none;
	transition: color 0.18s;

	b {
		color: ${blackOrange};
		font-weight: 700;
	}

	&:hover {
		color: ${blackOrange};
	}

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`

export const Main = styled.main`
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 12px 28px 56px;
`

export const Card = styled.div`
	display: grid;
	grid-template-columns: 0.84fr 1.16fr;
	width: 100%;
	max-width: 920px;
	background: ${white};
	border-radius: 28px;
	overflow: hidden;
	box-shadow: 0 18px 50px rgba(84, 18, 2, 0.1);

	@media (max-width: 760px) {
		grid-template-columns: 1fr;
		max-width: 440px;
	}
`

export const Hero = styled.section`
	background: ${blue};
	color: ${white};
	padding: 60px 48px 52px;
	display: flex;
	flex-direction: column;
	overflow: hidden;

	@media (max-width: 760px) {
		padding: 44px 32px 40px;
	}
`

export const HeroTitle = styled.h1`
	font-family: ${fontDisplay};
	font-weight: 900;
	font-size: 37px;
	line-height: 1.03;
	letter-spacing: -0.02em;
	color: ${white};
	margin: 0;
	text-wrap: balance;

	@media (max-width: 760px) {
		font-size: 33px;
	}
`

export const Tagline = styled.p`
	margin: 18px 0 0;
	font-size: 15.5px;
	line-height: 1.5;
	color: rgba(255, 255, 255, 0.8);
	max-width: 300px;
	text-wrap: pretty;
`

export const Mark = styled.div`
	margin-top: auto;
	padding-top: 48px;
	display: flex;
	align-items: center;
	gap: 9px;
	opacity: 0.85;

	img {
		width: 22px;
		height: 22px;
	}

	b {
		font-family: ${fontDisplay};
		font-weight: 700;
		font-size: 15px;
		color: ${white};
	}
`

export const SignIn = styled.section`
	padding: 60px 48px 48px;
	display: flex;
	flex-direction: column;

	@media (max-width: 760px) {
		padding: 44px 32px 38px;
	}
`

export const SignInTitle = styled.h2`
	font-family: ${fontDisplay};
	font-weight: 900;
	font-size: 27px;
	line-height: 1.1;
	letter-spacing: -0.02em;
	color: ${blackOrange};
	margin: 0;
`

export const Lede = styled.p`
	margin: 9px 0 0;
	font-size: 15px;
	line-height: 1.55;
	color: ${inkLede};
	text-wrap: pretty;
`

export const GoogleSlot = styled.div`
	margin-top: 34px;
`

export const Divider = styled.div`
	display: flex;
	align-items: center;
	gap: 14px;
	margin: 30px 0;
	color: ${inkFaint};
	font-family: ${fontDisplay};
	font-weight: 700;
	font-size: 12.5px;
	letter-spacing: 0.1em;
	text-transform: uppercase;

	&::before,
	&::after {
		content: '';
		flex: 1;
		height: 1px;
		background: rgba(208, 95, 42, 0.16);
	}
`

export const QrRow = styled.div`
	display: flex;
	align-items: center;
	gap: 18px;
	background: ${backgroundOrange};
	border: 1px solid rgba(208, 95, 42, 0.12);
	border-radius: 18px;
	padding: 16px;
`

export const QrTile = styled.div`
	width: 108px;
	height: 108px;
	flex: none;
	background: ${white};
	border-radius: 12px;
	padding: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow:
		0 1px 2px rgba(38, 18, 2, 0.06),
		0 0 0 1px rgba(38, 18, 2, 0.04);
`

export const QrCodeImage = styled(QRCode)`
	display: block;
	width: 100%;
	height: 100%;
`

export const QrText = styled.div`
	b {
		display: block;
		font-family: ${fontDisplay};
		font-weight: 700;
		font-size: 15.5px;
		color: ${blackOrange};
	}

	p {
		margin: 5px 0 0;
		font-size: 13.5px;
		line-height: 1.45;
		color: ${inkSoft};
		text-wrap: pretty;
	}
`

export const Foot = styled.div`
	margin-top: auto;
	padding-top: 34px;
	font-size: 14px;
	color: ${inkSoft};
	text-align: center;

	a {
		color: ${darkOrange};
		font-family: ${fontDisplay};
		font-weight: 700;
		text-decoration: none;
	}

	a:hover {
		text-decoration: underline;
	}
`

const spin = keyframes`
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
`

export const LoadingSpinner = styled.div`
	width: 36px;
	height: 36px;
	border: 3px solid rgba(208, 95, 42, 0.16);
	border-top-color: ${orange};
	border-radius: 50%;
	animation: ${spin} 1s linear infinite;
`
